/* eslint-disable react/prop-types */

import React from 'react';
import { Image, Loader2, AlertTriangle, Clock, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateHelper } from '../../utils/dateHelpers';
import { Text, Status, Button } from '../ui';
import usePostErrorStore from '../../store/usePostErrorStore';
import { generateMailtoLink } from '../../utils/emailTemplateHelper';
import { useOnboardingStore } from '../../store/useOnboardingStore';

const PostCard = ({ post, onClick, onCancel }) => {
  const { id: postId, aspectRatio, status, path, imageUrl, scheduledAt } = post;
  const postError = usePostErrorStore((state) => state.postErrors[postId]);
  const clearPostError = usePostErrorStore((state) => state.clearPostError);
  const queryClient = useQueryClient();

  const handleTryAgain = () => {
    clearPostError(postId);
    queryClient.invalidateQueries({ queryKey: ['posts'] });
  };

  const { userEmail: email } = useOnboardingStore();

  const handleContactSupport = () => {
    const mailtoLink = generateMailtoLink('post_regeneration_error', email, postId);
    window.location.href = mailtoLink;
  };

  const typeLabel = aspectRatio === '9:16' ? 'Story' : 'Feed';
  const scheduleLabel = scheduledAt
    ? formatDateHelper(scheduledAt, 'HH:mm a MMM do')
    : 'To be scheduled';
  const isCancellable = status === 'approved' || status === 'scheduled';

  return (
    <div
      className={`${
        typeLabel === 'Story' ? 'tour-card-for-story' : 'tour-card-for-feed'
      } rounded-xl overflow-hidden flex flex-col h-full`}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        className='p-4 flex justify-between items-start'
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <div className='space-y-1'>
          <Status status={postError ? 'stopped' : status} />
          <div className='flex items-center gap-1 mt-1'>
            <Clock className='w-3.5 h-3.5 shrink-0' style={{ color: 'var(--brand)' }} />
            <span className='text-xs font-semibold' style={{ color: 'var(--text-primary)' }}>
              {scheduleLabel}
            </span>
          </div>
        </div>
        <div className='flex items-center gap-1'>
          {isCancellable && onCancel && (
            <button
              type='button'
              title='Cancel post'
              onClick={(e) => {
                e.stopPropagation();
                onCancel(postId);
              }}
              className='p-1 rounded-md transition-colors'
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <X className='w-4 h-4' />
            </button>
          )}
          <Text
            variant='caption'
            className='px-2 py-0.5 rounded'
            style={{
              background: 'var(--bg-surface)',
              color: 'var(--text-body)',
              border: '1px solid var(--border)',
            }}
          >
            {typeLabel}
          </Text>
        </div>
      </div>

      {postError ? (
        <div
          className='flex-grow flex flex-col items-center justify-center p-6'
          style={{
            background: 'rgba(234,179,8,0.08)',
            borderTop: '1px solid rgba(234,179,8,0.3)',
          }}
        >
          <AlertTriangle className='w-8 h-8 mb-2' style={{ color: '#ca8a04' }} />
          <Text variant='caption' className='text-center' style={{ color: '#ca8a04' }}>
            {postError}
          </Text>
          <div className='mt-3 flex items-center flex-col gap-2'>
            <Button variant='primary' size='sm' onClick={handleTryAgain}>
              Try Again
            </Button>
            <Button variant='text' size='sm' onClick={handleContactSupport}>
              Contact Support
            </Button>
          </div>
        </div>
      ) : (
        <button
          type='button'
          onClick={onClick}
          className='flex-grow flex items-center justify-center p-6 transition-colors cursor-pointer'
          style={{ background: 'var(--bg-surface)' }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-card-alt)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--bg-surface)')}
          disabled={status === 'draft'}
        >
          <div
            className={`w-full ${
              aspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-[1/1]'
            } rounded-md shadow-sm relative overflow-hidden flex items-center justify-center`}
            style={{ background: 'var(--bg-input)' }}
          >
            {path || imageUrl ? (
              <img
                src={path || imageUrl}
                alt='Post preview'
                className='w-full h-full object-cover'
              />
            ) : (
              <div className='flex flex-col items-center justify-center gap-2' style={{ color: 'var(--text-muted)' }}>
                <Image className='w-8 h-8' />
                <Text variant='caption' style={{ color: 'var(--text-body)' }}>
                  Preview unavailable
                </Text>
              </div>
            )}

            {status === 'draft' ? (
              <div className='absolute inset-0 flex flex-col backdrop-blur-sm items-center justify-center gap-2' style={{ background: 'rgba(0,0,0,0.25)' }}>
                <Loader2 className='w-6 h-6 animate-spin' style={{ color: 'var(--brand)' }} />
                <Text variant='captionNeutral' className='text-base font-semibold' style={{ color: 'var(--text-primary)' }}>
                  Generating...
                </Text>
              </div>
            ) : null}
          </div>
        </button>
      )}
    </div>
  );
};

export default PostCard;
